/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { ReactComponent as NoConnectionIcon } from '../../../assets/svg/disconnect.svg';
import { QSButtonBase, QSTypography } from '../../atoms';
import { FC, useEffect, useRef, useState } from 'react';
import { ButtonProps } from '@mui/material';
import Loader from '../../molecules/loader';
import {
  trackSocketEventActionType,
  SocketEventActionType,
  isConnectionLostBarSuppressed,
} from '../../../utils/socket-event-logger';

const slideIn = keyframes`
  from {
    transform: translateX(calc(100% + 24px));
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const ConnectionLostBar: FC<{
  isVisible?: boolean;
  ctaText?: string;
  ctaProps?: ButtonProps;
  onRefresh?: () => void;
}> = ({ isVisible, ctaText = 'Try again', ctaProps, onRefresh }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const wasShownRef = useRef(false);

  useEffect(() => {
    if (isVisible) {
      wasShownRef.current = true;
      setIsDismissed(false);
      trackSocketEventActionType({ type: SocketEventActionType.CONNECTION_LOST_BAR_SHOWN });
    } else if (wasShownRef.current) {
      wasShownRef.current = false;
      trackSocketEventActionType({ type: SocketEventActionType.CONNECTION_LOST_BAR_HIDDEN });
    }
  }, [isVisible]);

  const handleRetryClick = () => {
    setIsLoading(true);
    onRefresh?.();
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  if (!isVisible || isDismissed || isConnectionLostBarSuppressed()) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      css={css`
        position: fixed;
        top: 16px;
        right: 16px;
        z-index: 1400;
        width: 360px;
        background: #fef6f6;
        border: 1px solid rgba(30, 41, 59, 0.14);
        border-radius: 8px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
        padding: 12px 12px 16px 16px;
        display: flex;
        gap: 12px;
        align-items: flex-start;
        animation: ${slideIn} 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
      `}
    >
      <NoConnectionIcon
        css={css`
          width: 20px;
          height: 20px;
          flex: 0 0 20px;
          color: #ef5350;
          margin-top: 2px;
        `}
      />

      <div
        css={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 2px;
          `}
        >
          <QSTypography
            css={css`
              color: #ef5350;
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
            `}
          >
            Connection lost
          </QSTypography>
          <QSTypography
            css={css`
              color: #7a7a7a;
              font-size: 14px;
              font-weight: 500;
              line-height: 1.45;
            `}
          >
            Check your internet connection &amp; retry
          </QSTypography>
        </div>

        <QSButtonBase
          onClick={handleRetryClick}
          {...ctaProps}
          css={css`
            align-self: flex-start;
            height: 36px;
            padding: 0 16px;
            border-radius: 4px;
            border: 1px solid rgba(30, 41, 59, 0.14);
            background: transparent;
            cursor: pointer;
            &:hover {
              background: rgba(45, 45, 45, 0.06);
            }
          `}
        >
          {isLoading ? (
            <Loader
              size={16}
              loaderProps={{
                style: {
                  color: '#2d2d2d',
                },
              }}
            />
          ) : (
            <QSTypography
              css={css`
                color: #2d2d2d;
                font-size: 14px;
                font-weight: 600;
                line-height: 1;
              `}
            >
              {ctaText}
            </QSTypography>
          )}
        </QSButtonBase>
      </div>

      <QSButtonBase
        aria-label="Dismiss"
        onClick={() => setIsDismissed(true)}
        css={css`
          flex: 0 0 32px;
          width: 32px;
          height: 32px;
          margin-left: -8px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          &:hover {
            background: rgba(45, 45, 45, 0.06);
          }
        `}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M5 5L15 15" stroke="#7A7A7A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 5L5 15" stroke="#7A7A7A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </QSButtonBase>
    </div>
  );
};

export default ConnectionLostBar;
